















import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { NavigationGuardNext, Route } from "vue-router";
import AccomodationUnitTypeForm from "@/components/property/AccomodationUnitTypeForm.vue";
import { AccomodationUnitTypeViewModel } from "@/shared/models/accomodationUnitTypeViewModel";
import GlobalFilterSensitiveMixin from "@/mixins/GlobalFilterSensitiveMixin";
import { Action, Getter } from "vuex-class";
import handleBrokenRuleAdapter from "@/shared/handleBrokenRuleAdapter";
import { Notification } from "@/store/modules/notifications";

@Component({
  components: {
    AccomodationUnitTypeForm,
  },
  mixins: [GlobalFilterSensitiveMixin],
  beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext) {
    const propertyId = Number.parseInt(to.params.propertyId);
    const propertyCategoryId = Number.parseInt(to.params.propertyCategoryId);
    const unitTypeId = to.params.unitTypeId;
    if (to.name === "New unit type") {
      next((vm: Vue) => {
        (vm as AccomodationUnitType).model =
          new AccomodationUnitTypeViewModel();
        (vm as AccomodationUnitType).model.propertyId = propertyId;
        (vm as AccomodationUnitType).model.categoryId = propertyCategoryId;
      });
    } else {
      Vue.axios
        .get<AccomodationUnitTypeViewModel>(
          "accomodationUnitTypes/GetForEdit/" + unitTypeId
        )
        .then((res) => {
          next((vm: Vue) => {
            const that = vm as AccomodationUnitType;
            that.model = Object.assign(
              new AccomodationUnitTypeViewModel(),
              res.data
            );
            that.breadcrumbs = [
              that.$t("Edit property").toString(),
              res.data.propertyName,
              res.data.name,
            ];
            that.setCustomBreadcrumbs &&
              that.setCustomBreadcrumbs(that.breadcrumbs);
          });
        })
        .catch(() => {
          next(() => next({ name: "Properties and units", replace: true }));
        });
    }
  },
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
    const self = this as any;
    if (to.name === "Edit unit type") {
      next();
    } else {
      const noChanges = self.accomodationUnitTypeForm.checkForChanges();
      if (noChanges) {
        next();
      } else {
        this.$store.dispatch("showNotification", {
          color: "warning",
          message: this.$t(
            "Changes pending. Are you sure you want to close the form?"
          ),
          actions: [
            {
              text: this.$t("Yes"),
              fun: () => {
                next();
                return true;
              },
            },
            {
              text: this.$t("No"),
              fun: () => {
                next(false);
                return true;
              },
            },
          ],
          timeout: 5000,
        });
      }
    }
  },
})
export default class AccomodationUnitType extends Vue {
  model = new AccomodationUnitTypeViewModel();

  breadcrumbs: string[] = [];

  @Ref("accomodationUnitTypeForm") accomodationUnitTypeForm?: {
    resetFormModel: () => void;
  };

  @Action("setCustomBreadcrumbs") setCustomBreadcrumbs?: (
    payload: string[] | undefined
  ) => Promise<void>;
  @Getter("customBreadcrumbs") customBreadcrumbs?: string[];

  async destroyed() {
    if (this.customBreadcrumbs == this.breadcrumbs) {
      this.setCustomBreadcrumbs && this.setCustomBreadcrumbs(undefined);
    }
  }

  isSubmitting = false;
  submit(model: AccomodationUnitTypeViewModel, overrides: string[] = []) {
    this.isSubmitting = true;

    if (!model.id) {
      this.axios
        .post("/AccomodationUnitTypes", model)
        .then((res) => {
          this.$store.dispatch("showNotification", {
            color: "success",
            message: this.$t("AccomodationUnitType.SaveSuccess"),
            timeout: 5000,
          });

          this.$router.replace({
            name: "Edit unit type",
            params: {
              propertyId: model.propertyId.toString() || "0",
              unitTypeId: res.data.toString() || "0",
            },
          });
        })
        .then(() => this.accomodationUnitTypeForm?.resetFormModel())
        .finally(() => (this.isSubmitting = false));
    } else {
      const handleCodes = ["RatePlan.DeleteValidation.OtaSync"];
      const adapter = handleBrokenRuleAdapter(this.axios, handleCodes);

      // prettier-ignore
      const formatBrokenRuleNames = (x: string[]) => x
      .map((h) => this.$t(h).toString())
      .map((th) => th.endsWith(".") ? th : th + ".")
      .join(" ");

      const isOverriding = overrides.length !== 0;

      this.axios
        .put(
          "/AccomodationUnitTypes",
          { ...model, overrideRules: overrides },
          {
            adapter: isOverriding ? undefined : adapter,
          }
        )
        .then(() => {
          this.$store.dispatch("showNotification", {
            color: "success",
            message: this.$t("AccomodationUnitType.SaveSuccess"),
            timeout: 5000,
          });

          this.axios
            .get<AccomodationUnitTypeViewModel>(
              "accomodationUnitTypes/GetForEdit/" + model.id
            )
            .then((res) => {
              this.model = { ...res.data };
            })
            .finally(() => (this.isSubmitting = false));
        })
        .then(() => this.accomodationUnitTypeForm?.resetFormModel())
        .catch((x) => {
          this.isSubmitting = false;
          if (x && x.isHandleBrokenRuleResult && !isOverriding) {
            this.$store.dispatch("showNotification", {
              color: "warning",
              message:
                // prettier-ignore
                `${formatBrokenRuleNames(x.handled)} ${this.$t("Do you wish to continue anyway?")}`,
              timeout: 30000,
              actions: [
                {
                  text: this.$t("Yes"),
                  fun: () => {
                    this.submit(model, x.handled);
                    return true;
                  },
                },
                { text: this.$t("Cancel"), fun: () => true },
              ],
            } as Notification);
          } else {
            this.$store.dispatch("showNotification", {
              color: "error",
              message: `${formatBrokenRuleNames(x.unhandled)}`,
              timeout: 5000,
            } as Notification);
          }
        });
    }
  }

  @Watch("sensitiveKey")
  tenantChanged(fresh: string, old: string) {
    const freshTenant = fresh.split("_")[0];
    const oldTenant = old.split("_")[0];

    if (freshTenant != oldTenant) {
      this.$router.push("Properties and units");
    }
  }
  async serviceAdded() {
    await Vue.axios
      .get("accomodationUnitTypes/GetForEdit/" + this.model.id)
      .then((res) => {
        this.model = res.data;
        this.$store.dispatch("showNotification", {
          color: "success",
          message: this.$t("AccomodationUnitType.AddOnService.AddSuccess"),
          timeout: 5000,
        });
      });
  }
}
